import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  errorToString,
  StatusData,
  TestProjectField,
  TestProjectFieldsResponse,
  TestProjectFieldUpdateResponse,
} from '@oneethos/shared'
import { toast } from 'react-toastify'
import api from '../api-client'
import { Spinner } from '@fluentui/react'
import { GrTest } from 'react-icons/gr'

type ProjectFieldInspectorProps = {
  project: StatusData
  onChange: (project: StatusData) => void
}

const valueAsString = (value: unknown): string => {
  if (value === undefined || value === null) {
    return ''
  }
  if (value instanceof Date) {
    return value.toISOString()
  }
  if (typeof value === 'object') {
    return JSON.stringify(value)
  }
  return String(value)
}

const ReadOnlyValue = ({ value }: { value: unknown }) => {
  if (value === undefined || value === null || value === '') {
    return <span className="text-body-secondary">Not set</span>
  }
  if (value instanceof Date) {
    return <span className="text-break">{value.toISOString()}</span>
  }
  if (typeof value === 'object') {
    return <code className="text-wrap">{JSON.stringify(value)}</code>
  }
  return <span className="text-break">{String(value)}</span>
}

export const ProjectFieldInspector = ({
  project,
  onChange,
}: ProjectFieldInspectorProps) => {
  const [fields, setFields] = useState<TestProjectField[]>([])
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [search, setSearch] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [savingField, setSavingField] = useState('')

  const loadFields = useCallback(async () => {
    setIsLoading(true)
    try {
      const response: TestProjectFieldsResponse = await api.get(
        `/loanapps/${project.id}/test-fields`,
      )
      setFields(response.fields)
      setDrafts({})
    } catch (error) {
      toast.error(errorToString(error), { autoClose: false })
    } finally {
      setIsLoading(false)
    }
  }, [project.id])

  useEffect(() => {
    loadFields()
  }, [loadFields])

  const visibleFields = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase()
    if (!normalizedSearch) {
      return fields
    }
    return fields.filter(field => [
      field.label,
      field.field,
      field.airtableField,
      field.source,
      field.type,
    ].some(value => value?.toLowerCase().includes(normalizedSearch)))
  }, [fields, search])

  const saveField = async (field: TestProjectField, clear = false) => {
    if (
      field.workflowCritical &&
      !window.confirm(`Update workflow-critical field "${field.label}"?`)
    ) {
      return
    }
    if (clear && !window.confirm(`Clear "${field.label}"?`)) {
      return
    }

    setSavingField(field.field)
    try {
      const draftValue = drafts[field.field] ?? valueAsString(field.value)
      const updateValue = field.type === 'boolean'
        ? draftValue === 'true'
        : draftValue
      const response: TestProjectFieldUpdateResponse = await api.patch(
        `/loanapps/${project.id}/test-fields`,
        {
          field: field.field,
          value: clear ? undefined : updateValue,
          clear,
        },
      )
      setFields(response.fields)
      setDrafts(current => {
        const next = { ...current }
        delete next[field.field]
        return next
      })
      onChange(new StatusData(response.project))
      toast.success(clear ? 'Test field cleared' : 'Test field saved')
    } catch (error) {
      toast.error(errorToString(error), { autoClose: false })
    } finally {
      setSavingField('')
    }
  }

  const renderInput = (field: TestProjectField) => {
    const value = drafts[field.field] ?? valueAsString(field.value)
    const onInputChange = (
      event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
    ) => {
      setDrafts(current => ({
        ...current,
        [field.field]: event.target.value,
      }))
    }

    if (field.options?.length) {
      return (
        <select
          aria-label={`Test value for ${field.label}`}
          className="form-select form-select-sm"
          value={value}
          onChange={onInputChange}
        >
          {!value ? <option value="">Not set</option> : null}
          {field.options.map(option => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      )
    }

    if (field.type === 'boolean') {
      return (
        <select
          aria-label={`Test value for ${field.label}`}
          className="form-select form-select-sm"
          value={value}
          onChange={onInputChange}
        >
          <option value="true">true</option>
          <option value="false">false</option>
        </select>
      )
    }

    const isLongText = /comments|notes|block/i.test(field.field)
    if (isLongText) {
      return (
        <textarea
          aria-label={`Test value for ${field.label}`}
          className="form-control form-control-sm"
          rows={2}
          value={value}
          onChange={onInputChange}
        />
      )
    }

    return (
      <input
        aria-label={`Test value for ${field.label}`}
        className="form-control form-control-sm"
        type={field.type === 'number' ? 'number' : 'text'}
        placeholder={
          field.type === 'date'
            ? 'MM/DD/YYYY'
            : field.type === 'timestamp'
              ? 'YYYY-MM-DDTHH:mm:ss.sssZ'
              : undefined
        }
        value={value}
        onChange={onInputChange}
      />
    )
  }

  return (
    <div className="mb-3">
      <div className="alert alert-secondary small d-flex align-items-center gap-2">
        <GrTest title="Test Only" />
        Test-only values are saved to this environment&apos;s project record. Airtable is
        not updated, and a later Airtable sync can overwrite these values.
      </div>
      <div className="d-flex gap-2 mb-3">
        <input
          aria-label="Search test project fields"
          className="form-control"
          placeholder="Search labels, model fields, types, or sources"
          value={search}
          onChange={event => setSearch(event.target.value)}
        />
        <button
          type="button"
          className="btn btn-outline-secondary"
          disabled={isLoading}
          onClick={loadFields}
        >
          Refresh
        </button>
      </div>
      {isLoading ? <Spinner label="Loading test project fields" /> : (
        <div className="table-responsive">
          <table className="table table-sm table-hover align-middle">
            <thead>
              <tr>
                <th>Label</th>
                <th>Model Field</th>
                <th>Source</th>
                <th>Current Value</th>
                <th>Test Value</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleFields.map(field => {
                const isDirty = drafts[field.field] !== undefined &&
                  drafts[field.field] !== valueAsString(field.value)
                const isSaving = savingField === field.field

                return (
                  <tr key={field.field}>
                    <td>
                      <div>{field.label}</div>
                      {field.airtableField ? (
                        <small className="text-body-secondary">
                          Airtable: {field.airtableField}
                        </small>
                      ) : null}
                    </td>
                    <td><code>{field.field}</code></td>
                    <td>
                      <span className="badge text-bg-light">{field.source}</span>
                      <small className="d-block text-body-secondary">{field.type}</small>
                    </td>
                    <td><ReadOnlyValue value={field.value} /></td>
                    <td>{field.editable ? renderInput(field) : <ReadOnlyValue value={field.value} />}</td>
                    <td>
                      {field.editable ? (
                        <div className="d-flex gap-1">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            disabled={!isDirty || Boolean(savingField)}
                            onClick={() => saveField(field)}
                          >
                            {isSaving ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            disabled={field.value === undefined || Boolean(savingField)}
                            onClick={() => saveField(field, true)}
                          >
                            Clear
                          </button>
                        </div>
                      ) : (
                        <span className="text-body-secondary">Read only</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          {!visibleFields.length ? (
            <p className="text-body-secondary">No fields match this search.</p>
          ) : null}
        </div>
      )}
    </div>
  )
}

export default ProjectFieldInspector

